import { useEffect, useMemo, useState } from "react";
import {
  getTiposPagoActivos,
  getPasarelasPagoActivas,
  obtenerMetodosEnvioReglas,
  crearMetodoPago,
  actualizarMetodoPago,
} from "../../../services/usuarios_tienda/metodos_pago";
import "./PaymentFormModal.css";

const TABS = {
  GENERAL: "general",
  CUENTAS: "cuentas",
  REGLAS: "reglas",
};

const emptyCuenta = () => ({
  tienda_cuenta_pago_id: null,
  banco: "",
  titular: "",
  numero_cuenta: "",
  tipo_cuenta: "",
  instrucciones: "",
  estado: 1,
});

const emptyRegla = () => ({
  tienda_metodo_pago_regla_id: null,
  tienda_metodo_envio_regla_id: "",
  disponible: 1,
  estado: 1,
});

const getArrayFromResponse = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response)) return response;
  return [];
};

function PaymentFormModal({
  isOpen,
  metodoData = null,
  onClose,
  onSubmit,
  onSaved,
}) {
  const [activeTab, setActiveTab] = useState(TABS.GENERAL);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [loadingCatalogs, setLoadingCatalogs] = useState(false);
  const [tiposPago, setTiposPago] = useState([]);
  const [reglasEnvio, setReglasEnvio] = useState([]);
  const [pasarelasActivas, setPasarelasActivas] = useState([]);

  const [showTipoPagoPicker, setShowTipoPagoPicker] = useState(false);
  const [showReglasPicker, setShowReglasPicker] = useState(false);
  const [searchTipoPago, setSearchTipoPago] = useState("");
  const [searchRegla, setSearchRegla] = useState("");

  const [form, setForm] = useState({
    tienda_metodo_pago_id: null,
    tipo_pago_id: "",
    tienda_pasarela_id: "",
    nombre_visible: "",
    descripcion: "",
    instrucciones: "",
    requiere_comprobante: 0,
    orden: "",
    estado: 1,
    tipo_pago_codigo: "",
    tipo_pago_nombre: "",
    requiere_pasarela: 0,
    cuentas_pago: [],
    reglas_pago: [],
  });

  useEffect(() => {
    if (!isOpen) return;

    let isMounted = true;

    const cargarCatalogos = async () => {
      try {
        setLoadingCatalogs(true);

        const [tiposRes, reglasRes] = await Promise.all([
          getTiposPagoActivos(),
          obtenerMetodosEnvioReglas(),
        ]);

        if (!isMounted) return;

        setTiposPago(getArrayFromResponse(tiposRes));
        setReglasEnvio(getArrayFromResponse(reglasRes));

        try {
          const pasarelasRes = await getPasarelasPagoActivas();
          if (!isMounted) return;
          setPasarelasActivas(getArrayFromResponse(pasarelasRes));
        } catch (pasarelaErr) {
          console.error("Error cargando pasarelas de pago activas:", pasarelaErr);
          if (isMounted) setPasarelasActivas([]);
        }
      } catch (err) {
        console.error("Error cargando catálogos de pago:", err);
      } finally {
        if (isMounted) {
          setLoadingCatalogs(false);
        }
      }
    };

    cargarCatalogos();

    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const metodo = metodoData || {};

    setForm({
      tienda_metodo_pago_id: metodo?.tienda_metodo_pago_id ?? null,
      tipo_pago_id: metodo?.tipo_pago_id ?? "",
      tienda_pasarela_id: metodo?.tienda_pasarela_id ?? "",
      nombre_visible: metodo?.nombre_visible ?? "",
      descripcion: metodo?.descripcion ?? "",
      instrucciones: metodo?.instrucciones ?? "",
      requiere_comprobante: Number(metodo?.requiere_comprobante ?? 0),
      orden: metodo?.orden ?? "",
      estado: Number(metodo?.estado ?? 1),
      tipo_pago_codigo: metodo?.tipo_pago_codigo ?? "",
      tipo_pago_nombre: metodo?.tipo_pago_nombre ?? "",
      requiere_pasarela: Number(metodo?.requiere_pasarela ?? 0),
      cuentas_pago: Array.isArray(metodo?.cuentas_pago)
        ? metodo.cuentas_pago.map((c) => ({
            tienda_cuenta_pago_id: c?.tienda_cuenta_pago_id ?? null,
            banco: c?.banco ?? "",
            titular: c?.titular ?? "",
            numero_cuenta: c?.numero_cuenta ?? "",
            tipo_cuenta: c?.tipo_cuenta ?? "",
            instrucciones: c?.instrucciones ?? "",
            estado: Number(c?.estado ?? 1),
          }))
        : [],
      reglas_pago: Array.isArray(metodo?.reglas_pago)
        ? metodo.reglas_pago.map((r) => ({
            tienda_metodo_pago_regla_id:
              r?.tienda_metodo_pago_regla_id ?? null,
            tienda_metodo_envio_regla_id:
              r?.tienda_metodo_envio_regla_id ?? "",
            disponible: Number(r?.disponible ?? 1),
            estado: Number(r?.estado ?? 1),
          }))
        : [],
    });

    setActiveTab(TABS.GENERAL);
    setSubmitting(false);
    setError("");
    setShowTipoPagoPicker(false);
    setShowReglasPicker(false);
    setSearchTipoPago("");
    setSearchRegla("");
  }, [isOpen, metodoData]);

  const isEdit = Boolean(form.tienda_metodo_pago_id);
  const modalTitle = isEdit ? "Editar método de pago" : "Crear método de pago";
  const requierePasarela = Number(form.requiere_pasarela) === 1;
  const puedeVerCuentas =
    !requierePasarela && String(form.tipo_pago_id || "").trim() !== "";

  const selectedTipoPago = useMemo(() => {
    return (
      tiposPago.find(
        (tp) => Number(tp?.tipo_pago_id) === Number(form.tipo_pago_id)
      ) || null
    );
  }, [tiposPago, form.tipo_pago_id]);

  const pasarelaActualEnCatalogo = useMemo(() => {
    const id = Number(form.tienda_pasarela_id);
    if (!Number.isInteger(id) || id <= 0) return true;
    return pasarelasActivas.some(
      (p) => Number(p?.tienda_pasarela_id) === id
    );
  }, [pasarelasActivas, form.tienda_pasarela_id]);

  const filteredTiposPago = useMemo(() => {
    const q = String(searchTipoPago || "").trim().toLowerCase();
    if (!q) return tiposPago;

    return tiposPago.filter((item) => {
      const nombre = String(item?.nombre || "").toLowerCase();
      const codigo = String(item?.codigo || "").toLowerCase();
      return nombre.includes(q) || codigo.includes(q);
    });
  }, [tiposPago, searchTipoPago]);

  const filteredReglas = useMemo(() => {
    const q = String(searchRegla || "").trim().toLowerCase();
    if (!q) return reglasEnvio;

    return reglasEnvio.filter((item) => {
      const metodo = String(
        item?.metodo_nombre_visible || item?.metodo_nombre || ""
      ).toLowerCase();

      const zona = String(
        item?.zona_nombre || item?.zona_codigo || ""
      ).toLowerCase();

      const id = String(item?.tienda_metodo_envio_regla_id || "").toLowerCase();

      return metodo.includes(q) || zona.includes(q) || id.includes(q);
    });
  }, [reglasEnvio, searchRegla]);

  const reglasSeleccionadasIds = useMemo(() => {
    return new Set(
      form.reglas_pago
        .filter((r) => Number(r.estado) === 1)
        .map((r) => Number(r.tienda_metodo_envio_regla_id))
    );
  }, [form.reglas_pago]);

  const cuentasVisibles = useMemo(() => {
    return form.cuentas_pago.filter((c) => Number(c.estado) === 1);
  }, [form.cuentas_pago]);

  const reglasVisibles = useMemo(() => {
    return form.reglas_pago.filter((r) => Number(r.estado) === 1);
  }, [form.reglas_pago]);

  const tabItems = [
    { key: TABS.GENERAL, label: "General" },
    {
      key: TABS.CUENTAS,
      label: `Cuentas (${cuentasVisibles.length})`,
      disabled: !puedeVerCuentas,
    },
    {
      key: TABS.REGLAS,
      label: `Reglas (${reglasVisibles.length})`,
      disabled: false,
    },
  ];

  const setField = (name, value) => {
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setField(name, value);
  };

  const handleCuentaChange = (visibleIndex, field, value) => {
    setForm((prev) => {
      const activos = [];
      prev.cuentas_pago.forEach((item, realIndex) => {
        if (Number(item.estado) === 1) {
          activos.push(realIndex);
        }
      });

      const realIndex = activos[visibleIndex];
      if (realIndex === undefined) return prev;

      const next = [...prev.cuentas_pago];
      next[realIndex] = {
        ...next[realIndex],
        [field]: value,
      };

      return { ...prev, cuentas_pago: next };
    });
  };

  const addCuenta = () => {
    setForm((prev) => ({
      ...prev,
      cuentas_pago: [...prev.cuentas_pago, emptyCuenta()],
    }));
  };

  const removeCuenta = (visibleIndex) => {
    setForm((prev) => {
      const activos = [];
      prev.cuentas_pago.forEach((item, realIndex) => {
        if (Number(item.estado) === 1) {
          activos.push(realIndex);
        }
      });

      const realIndex = activos[visibleIndex];
      if (realIndex === undefined) return prev;

      const next = [...prev.cuentas_pago];
      next[realIndex] = {
        ...next[realIndex],
        estado: 0,
      };

      return { ...prev, cuentas_pago: next };
    });
  };

  const handleReglaChange = (visibleIndex, field, value) => {
    setForm((prev) => {
      const activos = [];
      prev.reglas_pago.forEach((item, realIndex) => {
        if (Number(item.estado) === 1) {
          activos.push(realIndex);
        }
      });

      const realIndex = activos[visibleIndex];
      if (realIndex === undefined) return prev;

      const next = [...prev.reglas_pago];
      next[realIndex] = {
        ...next[realIndex],
        [field]: value,
      };

      return { ...prev, reglas_pago: next };
    });
  };

  const removeRegla = (visibleIndex) => {
    setForm((prev) => {
      const activos = [];
      prev.reglas_pago.forEach((item, realIndex) => {
        if (Number(item.estado) === 1) {
          activos.push(realIndex);
        }
      });

      const realIndex = activos[visibleIndex];
      if (realIndex === undefined) return prev;

      const next = [...prev.reglas_pago];
      next[realIndex] = {
        ...next[realIndex],
        estado: 0,
      };

      return { ...prev, reglas_pago: next };
    });
  };

  const selectTipoPago = (tipo) => {
    const requierePasarelaNuevo = Number(tipo?.requiere_pasarela ?? 0);
    const requiereComprobanteNuevo = Number(tipo?.requiere_comprobante ?? 0);

    setForm((prev) => ({
      ...prev,
      tipo_pago_id: tipo?.tipo_pago_id ?? "",
      tipo_pago_codigo: tipo?.codigo ?? "",
      tipo_pago_nombre: tipo?.nombre ?? "",
      requiere_pasarela: requierePasarelaNuevo,
      requiere_comprobante: requiereComprobanteNuevo,
      tienda_pasarela_id:
        requierePasarelaNuevo === 1 ? prev.tienda_pasarela_id : "",
      cuentas_pago:
        requierePasarelaNuevo === 1
          ? prev.cuentas_pago.map((c) => ({ ...c, estado: 0 }))
          : prev.cuentas_pago,
    }));

    setShowTipoPagoPicker(false);

    if (requierePasarelaNuevo === 1 && activeTab === TABS.CUENTAS) {
      setActiveTab(TABS.GENERAL);
    }
  };

  const addReglaDesdeCatalogo = (regla) => {
    const reglaId = Number(regla?.tienda_metodo_envio_regla_id);

    if (!Number.isInteger(reglaId) || reglaId <= 0) return;
    if (reglasSeleccionadasIds.has(reglaId)) return;

    setForm((prev) => ({
      ...prev,
      reglas_pago: [
        ...prev.reglas_pago,
        {
          ...emptyRegla(),
          tienda_metodo_envio_regla_id: reglaId,
          disponible: 1,
          estado: 1,
        },
      ],
    }));
  };

  const getReglaLabel = (reglaId) => {
    const regla = reglasEnvio.find(
      (r) => Number(r?.tienda_metodo_envio_regla_id) === Number(reglaId)
    );

    if (!regla) return `Regla #${reglaId}`;

    const metodo =
      String(regla?.metodo_nombre_visible || "").trim() ||
      String(regla?.metodo_nombre || "").trim() ||
      "Método de envío";

    const zona =
      String(regla?.zona_nombre || "").trim() ||
      String(regla?.zona_codigo || "").trim() ||
      "Zona no especificada";

    return `${metodo} · ${zona}`;
  };

  const validateForm = () => {
    if (!String(form.tipo_pago_id).trim()) {
      return "Debes seleccionar un tipo de pago.";
    }

    if (!String(form.nombre_visible).trim()) {
      return "Debes ingresar el nombre visible.";
    }

    if (requierePasarela && !String(form.tienda_pasarela_id).trim()) {
      return "Este tipo de pago requiere una pasarela activa.";
    }

    if (!requierePasarela) {
      for (const cuenta of form.cuentas_pago.filter((c) => Number(c.estado) === 1)) {
        const tieneAlgo =
          String(cuenta.banco || "").trim() ||
          String(cuenta.titular || "").trim() ||
          String(cuenta.numero_cuenta || "").trim() ||
          String(cuenta.tipo_cuenta || "").trim() ||
          String(cuenta.instrucciones || "").trim();

        if (tieneAlgo && !String(cuenta.numero_cuenta || "").trim()) {
          return "Cada cuenta agregada debe tener número de cuenta.";
        }
      }
    }

    const ids = form.reglas_pago
      .filter((r) => Number(r.estado) === 1)
      .map((r) => String(r.tienda_metodo_envio_regla_id || "").trim())
      .filter(Boolean);

    if (new Set(ids).size !== ids.length) {
      return "No puedes repetir la misma regla de envío.";
    }

    return "";
  };

  const buildPayload = () => {
    return {
      tipo_pago_id: Number(form.tipo_pago_id),
      tienda_pasarela_id: requierePasarela
        ? Number(form.tienda_pasarela_id)
        : null,
      nombre_visible: String(form.nombre_visible || "").trim(),
      descripcion: String(form.descripcion || "").trim() || null,
      instrucciones: String(form.instrucciones || "").trim() || null,
      requiere_comprobante: Number(form.requiere_comprobante) === 1 ? 1 : 0,
      orden: String(form.orden).trim() === "" ? null : Number(form.orden),
      estado: Number(form.estado) === 1 ? 1 : 0,
      cuentas_pago: requierePasarela
        ? form.cuentas_pago.map((c) => ({
            tienda_cuenta_pago_id: c.tienda_cuenta_pago_id || null,
            banco: String(c.banco || "").trim() || null,
            titular: String(c.titular || "").trim() || null,
            numero_cuenta: String(c.numero_cuenta || "").trim() || null,
            tipo_cuenta: String(c.tipo_cuenta || "").trim() || null,
            instrucciones: String(c.instrucciones || "").trim() || null,
            estado: 0,
          }))
        : form.cuentas_pago.map((c) => ({
            tienda_cuenta_pago_id: c.tienda_cuenta_pago_id || null,
            banco: String(c.banco || "").trim() || null,
            titular: String(c.titular || "").trim() || null,
            numero_cuenta: String(c.numero_cuenta || "").trim() || null,
            tipo_cuenta: String(c.tipo_cuenta || "").trim() || null,
            instrucciones: String(c.instrucciones || "").trim() || null,
            estado: Number(c.estado) === 1 ? 1 : 0,
          })),
      reglas_pago: form.reglas_pago
        .map((r) => ({
          tienda_metodo_pago_regla_id: r.tienda_metodo_pago_regla_id || null,
          tienda_metodo_envio_regla_id: Number(r.tienda_metodo_envio_regla_id),
          disponible: Number(r.disponible) === 1 ? 1 : 0,
          estado: Number(r.estado) === 1 ? 1 : 0,
        }))
        .filter((r) => Number.isInteger(r.tienda_metodo_envio_regla_id)),
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setError("");
    setSubmitting(true);

    try {
      const payload = buildPayload();
      let response;

      if (typeof onSubmit === "function") {
        response = await onSubmit(payload, form);
      } else if (isEdit) {
        response = await actualizarMetodoPago(form.tienda_metodo_pago_id, payload);
      } else {
        response = await crearMetodoPago(payload);
      }

      onSaved?.(response);
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "No se pudo guardar el método de pago."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="tienda-modal__overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="payform-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="payform-header">
          <div>
            <div className="payform-eyebrow">
              {isEdit ? "Configuración existente" : "Nuevo método"}
            </div>
            <h3 className="payform-title">{modalTitle}</h3>
            <p className="payform-subtitle">
              Configura el método principal, sus cuentas y sus reglas de disponibilidad.
            </p>
          </div>

          <button
            type="button"
            className="payform-close"
            onClick={onClose}
            disabled={submitting}
          >
            ✕
          </button>
        </div>

        <div className="payform-overview">
          <div className="payform-overview-card">
            <span className="payform-overview-label">Tipo</span>
            <strong>{selectedTipoPago?.nombre || "Sin seleccionar"}</strong>
            <small>
              {requierePasarela ? "Opera con pasarela" : "Opera con cuentas manuales"}
            </small>
          </div>

          <div className="payform-overview-card">
            <span className="payform-overview-label">Comprobante</span>
            <strong>
              {Number(form.requiere_comprobante) === 1 ? "Requerido" : "No requerido"}
            </strong>
            <small>Define si el cliente debe adjuntar soporte</small>
          </div>

          <div className="payform-overview-card">
            <span className="payform-overview-label">Estado</span>
            <strong>{Number(form.estado) === 1 ? "Activo" : "Inactivo"}</strong>
            <small>Disponibilidad actual del método</small>
          </div>

          <div className="payform-overview-card">
            <span className="payform-overview-label">Configuración</span>
            <strong>
              {requierePasarela
                ? `${pasarelasActivas.length} pasarela(s) activa(s)`
                : `${cuentasVisibles.length} cuenta(s) activa(s)`}
            </strong>
            <small>
              {requierePasarela ? "Cobro automatizado" : "Cobro manual registrado"}
            </small>
          </div>
        </div>

        <div className="payform-tabs">
          {tabItems.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`payform-tab ${
                activeTab === tab.key ? "is-active" : ""
              } ${tab.disabled ? "is-disabled" : ""}`}
              onClick={() => !tab.disabled && setActiveTab(tab.key)}
              disabled={tab.disabled}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {error ? <div className="payform-error">{error}</div> : null}

        <form onSubmit={handleSubmit} className="payform-body">
          <div className="payform-layout">
            <div className="payform-main">
              {activeTab === TABS.GENERAL && (
                <section className="payform-section">
                  <div className="payform-block">
                    <div className="payform-block-head">
                      <h4>Identidad del método</h4>
                      <p>
                        Define cómo se mostrará este método de pago dentro del checkout.
                      </p>
                    </div>

                    <div className="payform-grid payform-grid-2">
                      <div className="payform-field">
                        <label>Tipo de pago</label>

                        <button
                          type="button"
                          className="payform-picker-btn"
                          onClick={() => setShowTipoPagoPicker((v) => !v)}
                        >
                          <span>
                            {selectedTipoPago
                              ? selectedTipoPago?.nombre || "Sin nombre"
                              : "Seleccionar tipo de pago"}
                          </span>
                          <span
                            className={`payform-chevron ${
                              showTipoPagoPicker ? "is-open" : ""
                            }`}
                          >
                            â–¾
                          </span>
                        </button>

                        {showTipoPagoPicker && (
                          <div className="payform-picker-panel">
                            <input
                              type="text"
                              className="payform-search"
                              placeholder="Buscar tipo de pago..."
                              value={searchTipoPago}
                              onChange={(e) => setSearchTipoPago(e.target.value)}
                            />

                            <div className="payform-option-list">
                              {loadingCatalogs ? (
                                <div className="payform-option-empty">
                                  Cargando tipos de pago...
                                </div>
                              ) : filteredTiposPago.length === 0 ? (
                                <div className="payform-option-empty">
                                  No se encontraron tipos de pago.
                                </div>
                              ) : (
                                filteredTiposPago.map((tipo) => (
                                  <button
                                    key={tipo.tipo_pago_id}
                                    type="button"
                                    className={`payform-option ${
                                      Number(form.tipo_pago_id) === Number(tipo.tipo_pago_id)
                                        ? "is-selected"
                                        : ""
                                    }`}
                                    onClick={() => selectTipoPago(tipo)}
                                  >
                                    <div className="payform-option-main">
                                      <strong>{tipo.nombre}</strong>
                                    </div>
                                  </button>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="payform-field">
                        <label>Orden</label>
                        <input
                          name="orden"
                          value={form.orden}
                          onChange={handleChange}
                          placeholder="Ej. 1"
                        />
                      </div>

                      <div className="payform-field payform-grid-span-2">
                        <label>Nombre visible</label>
                        <input
                          name="nombre_visible"
                          value={form.nombre_visible}
                          onChange={handleChange}
                          placeholder="Ej. Transferencia bancaria"
                        />
                      </div>

                      <div className="payform-field payform-grid-span-2">
                        <label>Descripción</label>
                        <input
                          name="descripcion"
                          value={form.descripcion}
                          onChange={handleChange}
                          placeholder="Descripción opcional del método"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="payform-block">
                    <div className="payform-block-head">
                      <h4>Comportamiento del método</h4>
                      <p>
                        Configura instrucciones, comprobantes y estado general.
                      </p>
                    </div>

                    <div className="payform-grid payform-grid-2">
                      <div className="payform-field payform-grid-span-2">
                        <label>Instrucciones</label>
                        <textarea
                          name="instrucciones"
                          value={form.instrucciones}
                          onChange={handleChange}
                          placeholder="Ej. Envía tu comprobante al WhatsApp de la tienda"
                          rows={4}
                        />
                      </div>

                      <div className="payform-field">
                        <label>Requiere comprobante</label>
                        <select
                          name="requiere_comprobante"
                          value={form.requiere_comprobante}
                          onChange={handleChange}
                        >
                          <option value={1}>Sí</option>
                          <option value={0}>No</option>
                        </select>
                      </div>

                      <div className="payform-field">
                        <label>Estado</label>
                        <select
                          name="estado"
                          value={form.estado}
                          onChange={handleChange}
                        >
                          <option value={1}>Activo</option>
                          <option value={0}>Inactivo</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {requierePasarela && (
                    <div className="payform-block">
                      <div className="payform-block-head">
                        <h4>Pasarela asociada</h4>
                        <p>
                          Este tipo de pago utiliza procesamiento automático.
                        </p>
                      </div>

                      <div className="payform-grid payform-grid-2">
                        <div className="payform-field payform-grid-span-2">
                          <label>Pasarela de pago</label>
                          <select
                            name="tienda_pasarela_id"
                            value={
                              form.tienda_pasarela_id === "" ||
                              form.tienda_pasarela_id == null
                                ? ""
                                : String(form.tienda_pasarela_id)
                            }
                            onChange={handleChange}
                          >
                            <option value="">Selecciona una pasarela</option>
                            {!pasarelaActualEnCatalogo &&
                              String(form.tienda_pasarela_id || "").trim() !== "" && (
                                <option value={String(form.tienda_pasarela_id)}>
                                  ID {form.tienda_pasarela_id} (no está en la lista actual)
                                </option>
                              )}
                            {pasarelasActivas.map((p) => (
                              <option
                                key={p.tienda_pasarela_id}
                                value={String(p.tienda_pasarela_id)}
                              >
                                {String(p.nombre || "").trim() || "Pasarela"}
                                {p.codigo ? ` · ${p.codigo}` : ""}
                              </option>
                            ))}
                          </select>

                          {pasarelasActivas.length === 0 && (
                            <p className="payform-hint">
                              No hay pasarelas activas configuradas para esta tienda.
                              Configúralas antes de asociar un método con pasarela.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="payform-helper-box">
                    {selectedTipoPago ? (
                      <>
                        <strong>{selectedTipoPago?.nombre}</strong>
                        {" · "}
                        {Number(selectedTipoPago?.requiere_pasarela) === 1
                          ? "Este tipo usa pasarela, por lo que normalmente no necesita cuentas manuales."
                          : "Este tipo se maneja manualmente, por lo que puedes registrar una o varias cuentas de pago."}
                      </>
                    ) : (
                      "Selecciona primero un tipo de pago para que el formulario se adapte automáticamente."
                    )}
                  </div>
                </section>
              )}

              {activeTab === TABS.CUENTAS && (
                <section className="payform-section">
                  {!puedeVerCuentas ? (
                    <div className="payform-empty">
                      Este método usa pasarela o aún no tiene tipo de pago definido.
                      Las cuentas manuales solo aplican para métodos sin pasarela.
                    </div>
                  ) : (
                    <>
                      <div className="payform-section-top">
                        <div>
                          <h4>Cuentas de pago</h4>
                          <p>
                            Registra las cuentas bancarias o referencias que estarán
                            disponibles para el cliente.
                          </p>
                        </div>

                        <button
                          type="button"
                          className="payform-add-btn"
                          onClick={addCuenta}
                        >
                          Agregar cuenta
                        </button>
                      </div>

                      {cuentasVisibles.length === 0 ? (
                        <div className="payform-empty">
                          Aún no has agregado cuentas de pago.
                        </div>
                      ) : (
                        <div className="payform-stack">
                          {cuentasVisibles.map((cuenta, index) => (
                            <div
                              className="payform-card"
                              key={cuenta.tienda_cuenta_pago_id || `new-${index}`}
                            >
                              <div className="payform-card-top">
                                <div>
                                  <strong>Cuenta #{index + 1}</strong>
                                  <div className="payform-card-meta">
                                    {cuenta.banco || "Sin banco definido"} ·{" "}
                                    {cuenta.numero_cuenta || "Sin número"}
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  className="payform-remove-btn"
                                  onClick={() => removeCuenta(index)}
                                >
                                  Eliminar
                                </button>
                              </div>

                              <div className="payform-grid payform-grid-2">
                                <div className="payform-field">
                                  <label>Banco</label>
                                  <input
                                    value={cuenta.banco}
                                    onChange={(e) =>
                                      handleCuentaChange(index, "banco", e.target.value)
                                    }
                                    placeholder="Ej. Banco de Guatemala"
                                  />
                                </div>

                                <div className="payform-field">
                                  <label>Titular</label>
                                  <input
                                    value={cuenta.titular}
                                    onChange={(e) =>
                                      handleCuentaChange(index, "titular", e.target.value)
                                    }
                                    placeholder="Nombre del titular"
                                  />
                                </div>

                                <div className="payform-field">
                                  <label>Número de cuenta</label>
                                  <input
                                    value={cuenta.numero_cuenta}
                                    onChange={(e) =>
                                      handleCuentaChange(
                                        index,
                                        "numero_cuenta",
                                        e.target.value
                                      )
                                    }
                                    placeholder="Obligatorio"
                                  />
                                </div>

                                <div className="payform-field">
                                  <label>Tipo de cuenta</label>
                                  <input
                                    value={cuenta.tipo_cuenta}
                                    onChange={(e) =>
                                      handleCuentaChange(
                                        index,
                                        "tipo_cuenta",
                                        e.target.value
                                      )
                                    }
                                    placeholder="Ej. Monetaria / Ahorro"
                                  />
                                </div>

                                <div className="payform-field payform-grid-span-2">
                                  <label>Instrucciones</label>
                                  <textarea
                                    rows={3}
                                    value={cuenta.instrucciones}
                                    onChange={(e) =>
                                      handleCuentaChange(
                                        index,
                                        "instrucciones",
                                        e.target.value
                                      )
                                    }
                                    placeholder="Ej. Colocar nombre del cliente en referencia"
                                  />
                                </div>

                                <div className="payform-field">
                                  <label>Estado</label>
                                  <select
                                    value={cuenta.estado}
                                    onChange={(e) =>
                                      handleCuentaChange(index, "estado", e.target.value)
                                    }
                                  >
                                    <option value={1}>Activo</option>
                                    <option value={0}>Inactivo</option>
                                  </select>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </section>
              )}

              {activeTab === TABS.REGLAS && (
                <section className="payform-section">
                  <div className="payform-section-top">
                    <div>
                      <h4>Reglas de pago</h4>
                      <p>
                        Define en qué reglas de envío estará disponible este método.
                      </p>
                    </div>

                    <button
                      type="button"
                      className="payform-add-btn"
                      onClick={() => setShowReglasPicker((v) => !v)}
                    >
                      {showReglasPicker ? "Ocultar reglas" : "Agregar regla"}
                    </button>
                  </div>

                  {showReglasPicker && (
                    <div className="payform-picker-panel payform-picker-panel--rules">
                      <input
                        type="text"
                        className="payform-search"
                        placeholder="Buscar regla de envío..."
                        value={searchRegla}
                        onChange={(e) => setSearchRegla(e.target.value)}
                      />

                      <div className="payform-option-list">
                        {loadingCatalogs ? (
                          <div className="payform-option-empty">
                            Cargando reglas de envío...
                          </div>
                        ) : filteredReglas.length === 0 ? (
                          <div className="payform-option-empty">
                            No se encontraron reglas.
                          </div>
                        ) : (
                          filteredReglas.map((regla) => {
                            const reglaId = Number(regla?.tienda_metodo_envio_regla_id);
                            const selected = reglasSeleccionadasIds.has(reglaId);

                            const metodo =
                              regla?.metodo_nombre_visible ||
                              regla?.metodo_nombre ||
                              "Método de envío";

                            const zona =
                              regla?.zona_nombre ||
                              regla?.zona_codigo ||
                              "Zona no especificada";

                            return (
                              <button
                                key={reglaId}
                                type="button"
                                className={`payform-option ${
                                  selected ? "is-selected" : ""
                                }`}
                                onClick={() => addReglaDesdeCatalogo(regla)}
                                disabled={selected}
                              >
                                <div className="payform-option-main">
                                  <strong>{metodo}</strong>
                                  <span>{zona}</span>
                                </div>

                                <div className="payform-option-badges">
                                  <span className="payform-mini-badge is-slate">
                                    ID {reglaId}
                                  </span>

                                  {Number(regla?.requiere_cuenta) === 1 && (
                                    <span className="payform-mini-badge is-indigo">
                                      Requiere cuenta
                                    </span>
                                  )}

                                  {selected && (
                                    <span className="payform-mini-badge is-green">
                                      Agregada
                                    </span>
                                  )}
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}

                  {reglasVisibles.length === 0 ? (
                    <div className="payform-empty">
                      Aún no has agregado reglas de pago.
                    </div>
                  ) : (
                    <div className="payform-stack">
                      {reglasVisibles.map((regla, index) => (
                        <div
                          className="payform-card"
                          key={regla.tienda_metodo_pago_regla_id || `new-${index}`}
                        >
                          <div className="payform-card-top">
                            <div>
                              <strong>{getReglaLabel(regla.tienda_metodo_envio_regla_id)}</strong>
                              <div className="payform-card-meta">
                                Regla #{index + 1}
                              </div>
                            </div>

                            <button
                              type="button"
                              className="payform-remove-btn"
                              onClick={() => removeRegla(index)}
                            >
                              Eliminar
                            </button>
                          </div>

                          <div className="payform-grid payform-grid-3">
                            <div className="payform-field">
                              <label>Regla de envío</label>
                              <input
                                value={getReglaLabel(regla.tienda_metodo_envio_regla_id)}
                                readOnly
                              />
                            </div>

                            <div className="payform-field">
                              <label>Disponible</label>
                              <select
                                value={regla.disponible}
                                onChange={(e) =>
                                  handleReglaChange(index, "disponible", e.target.value)
                                }
                              >
                                <option value={1}>Sí</option>
                                <option value={0}>No</option>
                              </select>
                            </div>

                            <div className="payform-field">
                              <label>Estado</label>
                              <select
                                value={regla.estado}
                                onChange={(e) =>
                                  handleReglaChange(index, "estado", e.target.value)
                                }
                              >
                                <option value={1}>Activo</option>
                                <option value={0}>Inactivo</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              )}
            </div>

            <aside className="payform-sidebar">
              <div className="payform-sidebar-card">
                <h4>Resumen</h4>

                <div className="payform-summary-list">
                  <div className="payform-summary-item">
                    <span>Nombre visible</span>
                    <strong>{form.nombre_visible || "Pendiente"}</strong>
                  </div>

                  <div className="payform-summary-item">
                    <span>Tipo de pago</span>
                    <strong>{selectedTipoPago?.nombre || "Pendiente"}</strong>
                  </div>

                  <div className="payform-summary-item">
                    <span>Modo</span>
                    <strong>{requierePasarela ? "Pasarela" : "Manual"}</strong>
                  </div>

                  <div className="payform-summary-item">
                    <span>Cuentas activas</span>
                    <strong>{cuentasVisibles.length}</strong>
                  </div>

                  <div className="payform-summary-item">
                    <span>Reglas activas</span>
                    <strong>{reglasVisibles.length}</strong>
                  </div>

                  <div className="payform-summary-item">
                    <span>Orden</span>
                    <strong>{String(form.orden || "").trim() || "—"}</strong>
                  </div>
                </div>
              </div>

              <div className="payform-sidebar-card is-soft">
                <h4>Ayuda rápida</h4>
                <p>
                  {requierePasarela
                    ? "Este método procesa cobros con una pasarela activa. Normalmente no necesita cuentas manuales."
                    : "Este método funciona manualmente, así que conviene registrar una o varias cuentas de pago."}
                </p>
              </div>
            </aside>
          </div>

          <div className="payform-footer">
            <button
              type="button"
              className="payform-btn payform-btn-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancelar
            </button>

            <button
              type="submit"
              className="payform-btn payform-btn-primary"
              disabled={submitting}
            >
              {submitting
                ? "Guardando..."
                : isEdit
                ? "Actualizar método"
                : "Crear método"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PaymentFormModal;